import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/customer-ui/components/alert";
import { Button } from "@babackapp/customer-ui/components/button";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import type {
  ReturnRequestShippingInfoDto,
  ReturnRequestStatusDto,
} from "@babackapp/private-api-client";
import { TruckIcon } from "@heroicons/react/24/solid";
import { Download, PackageCheck, PackageX } from "lucide-react";
import { motion } from "motion/react";
import type { ReactNode } from "react";

type Props = {
  status: ReturnRequestStatusDto;
  shippingInfo: ReturnRequestShippingInfoDto;
};

type AlertConfig = {
  variant: "default" | "destructive" | "pending" | "info";
  icon: ReactNode;
  titleKey: string;
};

function resolveAlertConfig(status: ReturnRequestStatusDto): AlertConfig | null {
  if (status.cancelled.done) {
    return { variant: "destructive", icon: <PackageX />, titleKey: "returnRequests.details.status.cancelled" };
  }
  if (status.resolved.done) {
    return { variant: "default", icon: <PackageCheck />, titleKey: "returnRequests.details.status.resolved" };
  }
  if (
    status.treatedMerchant.done ||
    status.treatedLogistician.done ||
    status.partiallyTreatedMerchant.done ||
    status.partiallyTreatedLogistician.done
  ) {
    return { variant: "info", icon: <PackageCheck />, titleKey: "returnRequests.details.status.treated" };
  }
  if (status.shipped.done) {
    return { variant: "info", icon: <TruckIcon />, titleKey: "returnRequests.details.status.shipped" };
  }
  if (status.shipping.done || status.approved.done) {
    return { variant: "pending", icon: <TruckIcon />, titleKey: "returnRequests.details.status.approved" };
  }
  if (status.waiting.done) {
    return { variant: "pending", icon: <TruckIcon />, titleKey: "returnRequests.details.status.waiting" };
  }
  return null;
}

export function StatusAlert({ status, shippingInfo }: Props) {
  const t = useCustomText();
  const config = resolveAlertConfig(status);

  if (!config) return null;

  const labelUrl = shippingInfo.hasLogisticLabel ? shippingInfo.label_url : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Alert variant={config.variant}>
        {config.icon}
        <AlertTitle>{t(config.titleKey)}</AlertTitle>
        {labelUrl && (
          <AlertDescription>
            <Button
              variant="outline"
              size="sm"
              className="flex mt-4 text-foreground"
              asChild
            >
              <a href={labelUrl} target="_blank" rel="noopener noreferrer">
                <Download />
                {t("endProcess.carrier.downloadLabel")}
              </a>
            </Button>
          </AlertDescription>
        )}
      </Alert>
    </motion.div>
  );
}
